import { Tabs, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconUpload } from '@tabler/icons-react';
import React from 'react';

import { FileTable } from 'src/design/organisms/FileDrop/FileTable';
import {
  IAttachedFile,
  IAttachmentChange,
} from 'src/design/organisms/attachments/attachmentTypes';
import { DropPrompt } from 'src/design/molecules/DropPrompt';
import { UploadProblemAlert } from 'src/design/molecules/UploadProblemAlert';
import { useControlledAttachments } from 'src/design/organisms/attachments/useControlledAttachments';

export interface IFileDropProps {
  /** Controlled list of current attachments; the component keeps no copy. */
  files: IAttachedFile[];
  /** Fires once per upload/removal with the next list and what changed. */
  onChange: (files: IAttachedFile[], change: IAttachmentChange) => void;
  /** Superseded versions listed under the Versions tab; passing this prop at all (even an empty array) shows the Versions tab — callers compose persisted revisions with anything they have removed this session. */
  revisions?: IAttachedFile[];
  /** Blocks uploads and removals; downloads stay available. */
  disabled?: boolean;
  maxFiles?: number;
  uploadFile: (file: File) => Promise<IAttachedFile>;
  downloadFile: (id: string) => Promise<{ url: string; name: string }>;
  /** When set, the attachments table gains a "Description" column with an inline editor; the Versions table never shows it. */
  onCommentSave?: (
    file: IAttachedFile,
    comment: string,
  ) => void | Promise<void>;
  /** When set, current attachments carrying an `externalUrl` get a "Copy link" action (never shown on the Versions tab). */
  onCopyLink?: (file: IAttachedFile) => void;
}

/** @registry Controlled attachment list with drag/drop upload and an optional Versions tab; uploads via an injected `uploadFile`. `maxFiles === 1` is a single-slot replace (used for Versions/revision callers); `maxFiles > 1` appends up to capacity and refuses over-capacity drops visibly instead of wiping existing attachments. */
export const FileDrop: React.FC<IFileDropProps> = ({
  files,
  onChange,
  revisions,
  maxFiles = 100,
  disabled,
  uploadFile,
  downloadFile,
  onCommentSave,
  onCopyLink,
}) => {
  const [activeTab, setActiveTab] = React.useState<string>('attachments');

  const { singleSlot, remaining, upload } = useControlledAttachments({
    files,
    maxFiles,
    uploadFile,
    downloadFile,
    onChange,
  });
  const showDropzone = !disabled && (singleSlot || remaining > 0);

  const capacityHint =
    singleSlot && files.length > 0
      ? 'Uploading will replace the current file'
      : singleSlot
      ? 'Attach one file'
      : files.length === 0
      ? `Attach up to ${maxFiles} files`
      : `Attach up to ${remaining} more file${remaining === 1 ? '' : 's'}`;

  const removeFile = (file: IAttachedFile) => {
    onChange(
      files.filter((f) => f.id !== file.id),
      { added: [], removed: [file] },
    );
  };

  const AttachmentTable = (
    <FileTable
      files={files}
      emptyText="No Attachments"
      onDownload={(f) => upload.download(f)}
      onRemove={disabled ? undefined : removeFile}
      onCommentSave={onCommentSave}
      onCopyLink={onCopyLink}
    />
  );

  const RevisionsTable = (
    <FileTable
      files={revisions ?? []}
      emptyText="No Revisions"
      showVersion
      onDownload={(f) => upload.download(f)}
    />
  );

  return (
    <>
      {showDropzone && (
        <Dropzone
          onDrop={upload.onDrop}
          onReject={upload.onReject}
          loading={upload.loading}
          multiple={!singleSlot}
          maxFiles={singleSlot ? 1 : remaining}
          aria-label={upload.loading ? 'Uploading file' : 'Add file'}
        >
          <DropPrompt
            icon={
              <>
                <Dropzone.Accept>
                  <IconUpload size={52} stroke={1.5} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconUpload size={52} stroke={1.5} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconUpload size={52} stroke={1.5} />
                </Dropzone.Idle>
              </>
            }
            title={
              upload.loading
                ? 'Uploading...'
                : 'Drag file here or click to select'
            }
            description={capacityHint}
          />
        </Dropzone>
      )}
      {!disabled && !singleSlot && remaining === 0 && (
        <Text size="sm" c="dimmed" mt="xs">
          Attachment limit reached ({maxFiles})
        </Text>
      )}
      <UploadProblemAlert
        problems={upload.problems}
        onClear={upload.clearProblems}
      />
      {revisions === undefined ? (
        <>{AttachmentTable}</>
      ) : (
        <Tabs
          value={activeTab}
          onChange={(value) => setActiveTab(value || 'attachments')}
        >
          <Tabs.List>
            <Tabs.Tab value="attachments">Current</Tabs.Tab>
            <Tabs.Tab value="revisions">Versions ({revisions.length})</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="attachments">{AttachmentTable}</Tabs.Panel>
          <Tabs.Panel value="revisions">{RevisionsTable}</Tabs.Panel>
        </Tabs>
      )}
    </>
  );
};
